import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { ExternalLink } from "lucide-react";

/**
 * Modal iframe viewer for any URL (Google Drive share, S3 link, etc.).
 * - Opens as a popup (not a new tab / window)
 * - X button to close
 * - For Google Drive links, auto-rewrites to /preview so it embeds
 */
function toEmbedUrl(u) {
  if (!u) return "";
  // Google Drive share links → /preview
  const gd = u.match(/https?:\/\/drive\.google\.com\/file\/d\/([^/]+)/);
  if (gd) return `https://drive.google.com/file/d/${gd[1]}/preview`;
  // Google Docs / Sheets / Slides — swap /edit for /preview
  if (/https?:\/\/docs\.google\.com\//.test(u)) return u.replace(/\/edit.*$/, "/preview");
  return u;
}

export default function DocViewerModal({ open, onOpenChange, url, title = "Document" }) {
  const [embed, setEmbed] = useState("");
  useEffect(() => { if (open) setEmbed(toEmbedUrl(url)); }, [open, url]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-5xl w-[95vw] h-[85vh] p-0 overflow-hidden flex flex-col" data-testid="doc-viewer-modal">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="text-sm font-medium text-slate-900 truncate pr-4">{title}</div>
          <div className="flex items-center gap-1">
            <a href={url} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded hover:bg-slate-100 text-slate-600 flex items-center gap-1" title="Open source in a new tab"><ExternalLink className="w-3.5 h-3.5" />Source</a>
          </div>
        </div>
        <div className="flex-1 bg-slate-50">
          {embed ? (
            <iframe title={title} src={embed} className="w-full h-full border-0" allow="fullscreen" />
          ) : (
            <div className="p-6 text-slate-500 text-sm">No document link provided.</div>
          )}
        </div>
        <div className="text-[11px] text-slate-500 px-4 py-2 border-t border-slate-100 bg-slate-50">
          If the doc doesn't render, ensure the share link allows "Anyone with the link — Viewer" or click <a href={url} target="_blank" rel="noreferrer" className="text-orange-600 hover:underline">Source</a> to open externally.
        </div>
      </DialogContent>
    </Dialog>
  );
}
